import { useEffect, useState } from 'react';
import loadDigitalCardStyles from 'src/components/_common/DigitalCard/_utils/loadDigitalCardStyles';

/**
 * [디지털 카드 스타일시트 로딩 상태 관리 훅]
 *
 * @description
 * DigitalCard 컴포넌트에서 사용하는 CDN CSS의 로딩 상태를 관리합니다.
 * 컴포넌트 마운트 시 자동으로 CSS를 로드하고, 상태를 추적합니다.
 *
 * @returns
 * ```ts
 * {
 *   isStylesLoaded: boolean;    // 스타일시트 로딩 완료 여부
 *   isStylesError: boolean;     // 스타일시트 로딩 실패 여부
 * }
 * ```
 *
 * @usage
 * ```tsx
 * const { isStylesLoaded, isStylesError } = useDigitalCardStyles();
 *
 * if (!isStylesLoaded && !isStylesError) {
 *   return <Loading />;
 * }
 * ```
 *
 * @features
 * - 자동 CSS 로딩
 * - 로딩 상태 추적
 * - 에러 처리 (에러 발생 시 isStylesError = true)
 * - 에러 발생해도 계속 진행 가능 (폰트만 미적용)
 */
export const useDigitalCardStyles = () => {
  const [isStylesLoaded, setIsStylesLoaded] = useState(false);
  const [isStylesError, setIsStylesError] = useState(false);

  useEffect(() => {
    loadDigitalCardStyles()
      .then(() => {
        setIsStylesLoaded(true);
        setIsStylesError(false);
      })
      .catch((error) => {
        console.error('Failed to load Digital Card styles:', error);
        // 에러 발생 시에도 스타일을 "로드됨"으로 처리하여 계속 진행
        // 폰트만 적용되지 않고 나머지는 정상 작동
        setIsStylesLoaded(true);
        setIsStylesError(true);
      });
  }, []);

  return {
    isStylesLoaded,
    isStylesError,
  };
};
